import React, { useMemo, useRef, useState } from "react"
import { Anchor, Text, Title, UnstyledButton } from "@mantine/core"
import { useMediaQuery } from "@mantine/hooks"
import {
  captureVideoPlay,
  captureVideoProgress,
  type SiteVideoLocation,
  type SiteVideoProgress
} from "../../analytics"
import {
  siteVideoCaptionsUrl,
  siteVideoChapters,
  siteVideoHasPortrait,
  siteVideoLang,
  siteVideoPosterUrl,
  siteVideoUrl,
  type SiteVideoId,
  type SiteVideoOrientation
} from "../helpers/siteVideo"

// Phones get the portrait cut (burned-in captions, under a minute), anything
// wider the landscape one. Only the chosen file is ever requested.
const PHONE_QUERY = "(max-width: 767px)"
const PROGRESS_MARKS: SiteVideoProgress[] = [25, 50, 75, 100]

// 44 -> "0:44", 3725 -> "1:02:05".
const clock = (seconds: number): string => {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = String(seconds % 60).padStart(2, "0")
  return h ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`
}

// ?t=<seconds> on the page URL: where a chapter link (and the VideoObject's
// Clip urls, Google's "key moments") start the video.
const startParam = (): number => {
  const t = Number(new URLSearchParams(window.location.search).get("t"))
  return Number.isFinite(t) && t > 0 ? Math.floor(t) : 0
}

// "Deutsch", "日本語": the language's own name for the CC menu.
const captionLabel = (lang: string): string => {
  try {
    const name = new Intl.DisplayNames([lang], { type: "language" }).of(lang)
    return name ? name.charAt(0).toLocaleUpperCase(lang) + name.slice(1) : lang
  } catch {
    return lang
  }
}

// Click to play, never autoplay (the user, 2026-10-20: easy to ignore or
// skip). The poster is a frame of the agent at work, never the designed
// YouTube/X thumbnail, and with it nothing of the video loads until play. With a title it is a section of its
// own; without one, a figure with a small caption for a slot inside another
// section, like the homepage hero's art column. A cut with chapters lists them
// under the player; seekable lets ?t=<seconds> on the page URL start it there
// (only one video per page may claim the parameter).
export const SiteVideo: React.FC<{
  video: SiteVideoId
  title?: string
  caption?: string
  label: string
  sourceLocation: SiteVideoLocation
  className?: string
  seekable?: boolean
}> = ({ video, title, caption, label, sourceLocation, className, seekable }) => {
  const phone = useMediaQuery(PHONE_QUERY, undefined, { getInitialValueInEffect: false })
  const lang = useMemo(() => siteVideoLang(video), [video])
  // Rotating or resizing after play must not swap the file under the viewer.
  const [playedOrientation, setPlayedOrientation] = useState<SiteVideoOrientation | null>(null)
  const orientation: SiteVideoOrientation =
    playedOrientation ?? (phone && siteVideoHasPortrait(video) ? "portrait" : "landscape")
  const url = siteVideoUrl(video, lang, orientation)
  const captionsUrl = siteVideoCaptionsUrl(video, lang, orientation)
  const chapters = orientation === "landscape" ? siteVideoChapters(video, lang) : []
  // A media fragment, so the start costs nothing until play.
  const start = useMemo(() => (seekable ? startParam() : 0), [seekable])
  const player = useRef<HTMLVideoElement>(null)
  const sent = useRef(new Set<string>())

  const properties = { video, lang, orientation, source_location: sourceLocation }
  const once = (key: string, send: () => void) => {
    if (sent.current.has(key)) return
    sent.current.add(key)
    send()
  }

  const Wrapper = title ? "section" : "figure"
  return (
    <Wrapper className={["or-site-video", className].filter(Boolean).join(" ")}>
      {title ? (
        <Title order={2} className="or-demo-title">
          {title}
        </Title>
      ) : null}
      <video
        key={url}
        ref={player}
        className={`or-site-video-player or-site-video-${orientation}`}
        src={start ? `${url}#t=${start}` : url}
        poster={siteVideoPosterUrl(video, lang, orientation)}
        lang={lang}
        controls
        playsInline
        preload="none"
        aria-label={label}
        onPlay={() => {
          setPlayedOrientation(orientation)
          once(`play:${url}`, () => captureVideoPlay(properties))
        }}
        onTimeUpdate={(event) => {
          const { currentTime, duration } = event.currentTarget
          if (!duration) return
          const percent = (currentTime / duration) * 100
          PROGRESS_MARKS.forEach((mark) => {
            // 100 is "reached the last second", since timeupdate rarely lands on the end.
            const reached = mark === 100 ? duration - currentTime < 1 : percent >= mark
            if (reached) once(`${mark}:${url}`, () => captureVideoProgress(properties, mark))
          })
        }}
      >
        {/* Off until the viewer turns them on from the player's CC button. */}
        {captionsUrl ? <track kind="captions" src={captionsUrl} srcLang={lang} label={captionLabel(lang)} /> : null}
        <Anchor href={url}>Watch the video</Anchor>
      </video>
      {chapters.length ? (
        <ol className="or-site-video-chapters" aria-label="Chapters">
          {chapters.map((chapter) => (
            <li key={chapter.start}>
              <UnstyledButton
                className="or-site-video-chapter"
                onClick={() => {
                  const element = player.current
                  if (!element) return
                  element.currentTime = chapter.start
                  void element.play()
                }}
              >
                <span className="or-site-video-chapter-time">{clock(chapter.start)}</span>
                <span>{chapter.title}</span>
              </UnstyledButton>
            </li>
          ))}
        </ol>
      ) : null}
      {caption ? (
        <Text component="figcaption" className="or-site-video-caption" size="sm" c="dimmed">
          {caption}
        </Text>
      ) : null}
    </Wrapper>
  )
}
